import { Box, Checkbox, IconButton, Typography, useTheme } from "@mui/material";
import { Pencil, SquareCheck, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

type Props = {
  title?: string;
  titleAdornment?: React.ReactNode;
  onEditSection?: () => void;
  onRemoveSection?: () => void;
  actionsDisabled?: boolean;
  dragHandle?: React.ReactNode;
  children: React.ReactNode;
};

export default function LOAlternatives({
  title,
  titleAdornment,
  onEditSection,
  onRemoveSection,
  actionsDisabled = false,
  dragHandle,
  children,
}: Props) {
  const theme = useTheme();
  const { t } = useTranslation("lo-alternatives");

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "stretch",
        gap: 2,
        padding: "16px 20px",
        border: "1px solid transparent",
        borderRadius: "12px",
        backgroundColor: theme.palette.background.card || "#2A2A2A",
        transition: "border-color 0.15s ease",
        "&:hover": {
          borderColor: theme.palette.divider,
        },
      }}
    >
      {dragHandle}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          flex: 1,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  overflowWrap: "anywhere",
                  minWidth: 0,
                }}
              >
                {title || t("section.untitled")}
              </Typography>
              {titleAdornment}
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 1,
                color: theme.palette.text.disabled,
              }}
            >
              <Checkbox
                checked
                disabled
                checkedIcon={<SquareCheck size={18} />}
                sx={{ padding: 0 }}
              />
              <Typography sx={{ fontSize: "14px", color: "inherit" }}>
                {t("section.mandatory")}
              </Typography>
            </Box>
          </Box>

          {(onEditSection || onRemoveSection) && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {onEditSection && (
                <IconButton
                  aria-label={t("section.edit")}
                  title={t("section.edit")}
                  onClick={onEditSection}
                  disabled={actionsDisabled}
                  sx={{
                    width: 36,
                    height: 36,
                    color: theme.palette.primary.main,
                    "&:hover": {
                      color: theme.palette.primary.dark,
                      backgroundColor: theme.palette.action.hover,
                    },
                    "&.Mui-disabled": {
                      color: theme.palette.action.disabled,
                    },
                  }}
                >
                  <Pencil size={18} />
                </IconButton>
              )}
              {onRemoveSection && (
                <IconButton
                  aria-label={t("section.remove")}
                  title={t("section.remove")}
                  onClick={onRemoveSection}
                  disabled={actionsDisabled}
                  sx={{
                    width: 36,
                    height: 36,
                    color: theme.palette.primary.main,
                    "&:hover": {
                      color: theme.palette.primary.dark,
                      backgroundColor: theme.palette.action.hover,
                    },
                    "&.Mui-disabled": {
                      color: theme.palette.action.disabled,
                    },
                  }}
                >
                  <Trash2 size={18} />
                </IconButton>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}
